// ✅ FINAL FIX: WorkflowDetailsStep following NewList pattern exactly

import React, { useMemo, useCallback, useEffect, useState } from "react";
import { Button } from "@shared/ui/button";
import { ChevronRight } from "lucide-react";
import { DynamicForm } from "@shared/components/DynamicForm";
import {
  FormField,
  FormService,
  FormConfig,
} from "@shared/services/FormServices";
import { StepHelpers } from "@shared/components/Stepper";
import { useToast } from "@shared/hooks/use-toast";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useCreateWorkflow } from "@features/ux/work-flow/hooks";
import { usePatchCreateWorkflow } from "@features/ux/work-flow/hooks";

interface WorkflowDetailsStepProps {
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  onNext?: () => void;
  onPrevious?: () => void;
  readOnly?: boolean;
  existingWorkflow?: any;
  isEditMode?: boolean;
  mode?: string;
  workflowId?: string;
}

const WorkflowDetailsStep: React.FC<WorkflowDetailsStepProps> = ({
  stepHelpers,
  currentStepIndex = 0,
  onNext,
  onPrevious,
  readOnly = false,
  existingWorkflow,
  isEditMode,
  mode = "create",
  workflowId,
}) => {
  const { toast } = useToast();
  const utilityId = useRemoteUtilityId();

  const [localWorkflowId, setLocalWorkflowId] = useState<number | null>(null);

  // Mutations
  const createWorkflowMutation = useCreateWorkflow();
  const patchWorkflowMutation = usePatchCreateWorkflow();

  // ✅ CHANGE 1: Define fields WITHOUT explicit values (like NewListStepOne)
  const fields: FormField[] = [
    {
      name: "name",
      label: "Workflow Name",
      type: "text",
      required: true,
      placeholder: "e.g., New Customer Onboarding",
      helperText: "Enter a descriptive name for your workflow",
      disabled: readOnly || isEditMode,
      className: "col-span-2",
    },
    {
      name: "description",
      label: "Description",
      type: "textarea",
      required: true,
      placeholder: "Describe the purpose and functionality of this workflow...",
      rows: 3,
      helperText:
        "Provide a detailed description of what this workflow accomplishes",
      disabled: readOnly || isEditMode,
      className: "col-span-2",
    },
  ];

  // ✅ CHANGE 2: Create form with config (like NewListStepOne)
  const config: FormConfig = {
    fields,
    layout: "grid",
    showSubmitButton: false,
    showResetButton: false,
    resetOnSubmit: false,
    classes: {
      fieldContainer: "flex flex-col gap-1 mb-4",
      label: "text-sm text-gray-800 font-semibold",
      input: "rounded-md border px-3 py-2 border-gray-300 w-full",
      select: "rounded-md border px-3 py-2 border-gray-300",
      textarea: "rounded-md border px-3 py-2 border-gray-300",
      error: "text-sm text-red-500 mt-1",
      helperText: "text-xs text-gray-500 mt-1",
      buttonContainer: "hidden",
    },
  };

  const form = FormService.createForm(config);

  // ✅ CHANGE 3: Initialize from stepHelpers (exactly like NewListStepOne)
  useEffect(() => {
    const savedStepData = stepHelpers?.getStepData(currentStepIndex);

    if (savedStepData && Object.keys(savedStepData).length > 0) {
      const formData = {
        name: savedStepData.name || "",
        description: savedStepData.description || "",
      };

      FormService.populateForm(form, formData, fields);

      if (savedStepData.workflowId) {
        setLocalWorkflowId(savedStepData.workflowId);
      }
    }
  }, []); // Empty dependency - runs once

  // ✅ CHANGE 4: Initialize from API (exactly like NewListStepOne)
  useEffect(() => {
    if (!isEditMode || !existingWorkflow?.id) return;

    const formData = {
      name: existingWorkflow.name || "",
      description: existingWorkflow.description || "",
    };

    setLocalWorkflowId(existingWorkflow.id);

    // ✅ Use both methods like NewListStepOne
    FormService.populateForm(form, formData, fields);
    form.setValue("name", formData.name);
    form.setValue("description", formData.description);

    stepHelpers?.setStepData(currentStepIndex, {
      ...formData,
      workflowId: existingWorkflow.id,
      timestamp: new Date().toISOString(),
      stepIndex: currentStepIndex,
    });
  }, [isEditMode, existingWorkflow?.id]);

  // ✅ CHANGE 5: Empty field change handler (like NewListStepOne)
  const handleFieldChange = useCallback((fieldName: string, value: any) => {
    // Empty like NewListStepOne - no immediate stepHelpers saving
  }, []);

  // Create workflow
  const createWorkflow = useCallback(
    async (formData: any) => {
      try {
        const workflowData = {
          remoteUtilityId: utilityId,
          name: formData.name,
          description: formData.description,
          recipientType: "CONSUMER",
          recipientList: null,
        };

        const createdWorkflow = await createWorkflowMutation.mutateAsync(
          workflowData
        );
        const newWorkflowId = createdWorkflow?.id;

        if (newWorkflowId) {
          setLocalWorkflowId(newWorkflowId);

          stepHelpers?.setStepData(currentStepIndex, {
            ...formData,
            workflowId: newWorkflowId,
            timestamp: new Date().toISOString(),
            stepIndex: currentStepIndex,
          });

          toast({
            title: "Workflow Created",
            description: `"${formData.name}" workflow created successfully.`,
          });

          return { success: true, workflowId: newWorkflowId };
        }
      } catch (error: any) {
        toast({
          title: "Error Creating Workflow",
          description:
            error?.message || "Failed to create workflow. Please try again.",
          variant: "destructive",
        });
        return { success: false, error: error.message };
      }
    },
    [utilityId, createWorkflowMutation, stepHelpers, currentStepIndex, toast]
  );

  // Patch workflow
  const patchWorkflow = useCallback(
    async (formData: any, targetWorkflowId: number) => {
      try {
        const workflowData = {
          name: formData.name,
          description: formData.description,
        };

        await patchWorkflowMutation.mutateAsync({
          workflowId: targetWorkflowId,
          workflowData,
        });

        stepHelpers?.setStepData(currentStepIndex, {
          ...formData,
          workflowId: targetWorkflowId,
          timestamp: new Date().toISOString(),
          stepIndex: currentStepIndex,
        });

        toast({
          title: "Workflow Updated",
          description: `"${formData.name}" workflow updated successfully.`,
        });

        return { success: true, workflowId: targetWorkflowId };
      } catch (error: any) {
        toast({
          title: "Error Updating Workflow",
          description:
            error?.message || "Failed to update workflow. Please try again.",
          variant: "destructive",
        });
        return { success: false, error: error.message };
      }
    },
    [patchWorkflowMutation, stepHelpers, currentStepIndex, toast]
  );

  // ✅ CHANGE 6: Handle submit (like NewListStepOne)
  const handleSubmit = useCallback(
    async (data: any) => {
      if (isEditMode) {
        // In edit mode, just proceed to next step
        onNext?.();
        return;
      }

      if (!data.name || !data.description) {
        toast({
          title: "Missing Required Fields",
          description: "Please fill in the workflow name and description.",
          variant: "destructive",
        });
        return;
      }

      // Save current form data
      stepHelpers?.setStepData(currentStepIndex, {
        ...data,
        workflowId:
          localWorkflowId || (workflowId ? parseInt(workflowId) : null),
        timestamp: new Date().toISOString(),
        stepIndex: currentStepIndex,
      });

      const currentWorkflowId =
        localWorkflowId || (workflowId ? parseInt(workflowId) : null);

      if (mode === "create" && !currentWorkflowId) {
        const result = await createWorkflow(data);
        if (!result.success) return;
      } else if (currentWorkflowId) {
        const result = await patchWorkflow(data, currentWorkflowId);
        if (!result.success) return;
      }

      if (onNext) {
        onNext();
      }
    },
    [
      isEditMode,
      stepHelpers,
      currentStepIndex,
      localWorkflowId,
      workflowId,
      mode,
      createWorkflow,
      patchWorkflow,
      onNext,
      toast,
    ]
  );

  // Reset function
  const handleReset = useCallback(() => {
    FormService.populateForm(form, {}, fields);
  }, [form, fields]);

  const isMutationPending =
    createWorkflowMutation.isPending || patchWorkflowMutation.isPending;

  // Custom footer
  const renderCustomFooter = useCallback(() => {
    if (readOnly) return null;

    const currentWorkflowId =
      localWorkflowId || (workflowId ? parseInt(workflowId) : null);
    const isCreating = mode === "create" && !currentWorkflowId;

    let buttonText = "Next";
    if (isMutationPending) {
      buttonText = isCreating ? "Creating..." : "Updating...";
    }

    return (
      <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
        <Button
          type="submit"
          onClick={form.handleSubmit(handleSubmit)}
          className="flex items-center gap-2"
          disabled={isMutationPending}
        >
          {buttonText}
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    );
  }, [
    readOnly,
    form,
    handleSubmit,
    isMutationPending,
    localWorkflowId,
    workflowId,
    mode,
  ]);

  return (
    <div className="space-y-6 pt-4">
      {isEditMode && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> Basic workflow details cannot be modified in
            edit mode.
          </p>
        </div>
      )}

      <DynamicForm
        fields={fields}
        form={form}
        config={config}
        onSubmit={handleSubmit}
        onReset={handleReset}
        onFieldChange={handleFieldChange}
        gridColumns={2}
        responsiveColumns={{ sm: 1, md: 2 }}
        className="workflow-details-form"
        renderCustomFooter={renderCustomFooter}
      />
    </div>
  );
};

export default WorkflowDetailsStep;
